import { Button, Checkbox, Drawer, Radio, Typography } from "antd";
import Image from "next/image";
import { useIntl } from "react-intl";
import "./FmlmFilterDrawer.css"
import { getTransitIconPath } from "../../../Lib/Utils/booking";
import { useMemo } from "react";
import { IFmlmFilter, IFmlmView } from "../../../Orchestrator/Types/Interface/IFmlmView";
import { useRecState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { _eventBus } from "../../../core/events/EventBus";
import { fmlmViewInfoTree } from "../../../Orchestrator/ConstantTree/FmlmSearch/FmlmViewInformationTree";
import { SearchStateType } from "../../../Orchestrator/Types/Index";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";

interface IFmlmFilterDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const FMLM_FILTER_TRANSPORT_MODES = [
  { value: "car-taxi", label: "Car/Taxi", translateKey: "fmlmFilterLabel.car/taxi", modes: ["CAR"], disabled: false, excludeCode: "DRIVING", exclusionType: "origin" },
  { value: "shuttle", label: "Shuttle", translateKey: "fmlmFilterLabel.shuttle", modes: ["SHUTTLE"], disabled: false, excludeCode: "SHUTTLE", exclusionType: "city" },
  { value: "public-transport", label: "Public Transport", translateKey: "fmlmFilterLabel.public-transport", modes: ["METRO", "BUS"], disabled: false, excludeCode: "TRANSIT", exclusionType: "city" },
] as const;
export type TFmlmFilterTransportMode = typeof FMLM_FILTER_TRANSPORT_MODES[number]['value'];

export const FMLM_FILTER_SORT_OPTIONS = [
  { value: "default", label: "Default", translationKey: "fmlmFilterLabel.default", disabled: false },
  { value: "FASTEST", label: "Fastest", translationKey: "fmlmFilterLabel.fastest", disabled: false },
  { value: "FEWEST_TRANSFERS", label: "Less Transfers", translationKey: "fmlmFilterLabel.fewest-transfers", disabled: false },
  { value: "ENVIRONMENTAL_IMPACT", label: "Eco Friendly", translationKey: "fmlmFilterLabel.environmental-impact", disabled: false },
] as const;
export type TFmlmFilterSortOptions = typeof FMLM_FILTER_SORT_OPTIONS[number]['value'];

export const DEFAULT_FMLM_FILTER_STATE: IFmlmFilter = {
  hour: 8,
  minute: 0,
  flexHr: 1,
  mode: "leave",
  originTransportMode: FMLM_FILTER_TRANSPORT_MODES.filter(mode => !mode.disabled).map(mode => mode.value),
  destinationTransportMode: FMLM_FILTER_TRANSPORT_MODES.filter(mode => !mode.disabled).map(mode => mode.value),
  sortBy: "default",
}

export const hasFmlmFilterChanged = (fmlmFilter: IFmlmFilter) => {
  const compareFields = ["originTransportMode", "destinationTransportMode", "sortBy"];

  const isEqual = (a: any, b: any): boolean => {
    if (typeof a !== typeof b) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every(item => b.includes(item)) && b.every(item => a.includes(item));
    }
    if (a && typeof a === "object" && b && typeof b === "object") {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;
      return aKeys.every(key => isEqual(a[key], b[key]));
    }
    return a === b;
  };

  return !compareFields.every(field =>
    isEqual(
      fmlmFilter[field as keyof IFmlmFilter],
      DEFAULT_FMLM_FILTER_STATE[field as keyof IFmlmFilter]
    )
  );
}

const FmlmFilterDrawer = (props: IFmlmFilterDrawerProps) => {
  const { open, onClose } = props;
  const intl = useIntl();

  const [search] = useRecState<SearchStateType>("searchStateAtom");
  const [fmlmFilter] = useRecState<IFmlmFilter>("fmlmFilterAtom")
  const [fmlmViewList] = useRecState<IFmlmView[]>("fmlmViewInformationAtom");
  const [trainType] = useRecState<keyof typeof TrainTypeEnum>("trainTypeAtom");

  const updateFmlmFilter = (filter: Partial<IFmlmFilter>) => {
    const newFmlmFilter = {
      ...fmlmFilter,
      ...filter
    }

    _eventBus.emit({
      eventName: fmlmViewInfoTree.Filter.UpdateFilter,
      data: newFmlmFilter
    })
  }

  const handleSortByChange = (e: any) => {
    let value = e.target.value;
    updateFmlmFilter({ sortBy: value })
  };


  const handleChangeTransportationMode = (type: "originTransportMode" | "destinationTransportMode") => (e: any) => {
    const value = e.target.value;
    const updatedModes = fmlmFilter[type].includes(value)
      ? fmlmFilter[type].filter((mode) => mode !== value)
      : [...fmlmFilter[type], value];

    updateFmlmFilter({
      [type]: updatedModes
    })
  };

  const isFmlmFilterActive = useMemo(() => hasFmlmFilterChanged(fmlmFilter), [fmlmFilter])

  const handleClearFilter = () => updateFmlmFilter(DEFAULT_FMLM_FILTER_STATE)

  const handleGetOffers = () => {
    setTimeout(() => {
      _eventBus.emit({
        eventName: fmlmViewInfoTree.Filter.GetFmlmOffers,
        data: {
          trainType: trainType,
          search: search,
          fmlmFilter: fmlmFilter,
          isTimeVisible: false,
          fmlmViewList: fmlmViewList
        }
      })

      onClose();
    })
  }

  return (
    <Drawer
      title={
        <span className="fs-20 fw-600">
          {intl.formatMessage({ id: "filter" })}
        </span>
      }
      classNames={{
        header: "border-color-00000033",
        body: "p-20 pt-20"
      }}
      onClose={onClose}
      open={open}
      mask={{ closable: false }}
      closeIcon={null}
      width={"407px"}
      extra={
        <Button
          onClick={onClose}
          variant="text"
          type="text"
          className="center-btn-icon"
          icon={
            <Image
              src="/assets/search_icons/close.svg"
              height={18}
              width={18}
              alt="close"
            />
          }
        />
      }
    >
      <div className="fmlm-filter-drawer-container">
        <div className="d-flex flex-col gap-10 transport-mode-container">
          <Typography.Title level={5} className="fw-600 color-25272A mb-0 lh-normal">
            {intl.formatMessage({ id: 'modeOfTransportationAtOrigin' })}
          </Typography.Title>
          {FMLM_FILTER_TRANSPORT_MODES.map((mode) => (
            <div className="checkbox-container d-flex w-100 align-items-center justify-content-between" key={mode.label}>
              <Checkbox
                className="checkbox-21 w-100"
                checked={fmlmFilter.originTransportMode.includes(mode.value)}
                value={mode.value}
                onChange={handleChangeTransportationMode("originTransportMode")}
                disabled={mode.disabled}
              >
                <div className="d-flex align-items-center justify-content-between w-100">
                  <span className="fs-16 ff-inter fw-500 ml-5 lh-normal">
                    {intl.formatMessage({ id: mode.translateKey, defaultMessage: mode.label })}
                  </span>
                  <div>
                    {
                      mode.modes.map((transitMode) => (
                        <Image
                          key={transitMode}
                          src={getTransitIconPath(transitMode, "red")}
                          alt={transitMode}
                          width={28}
                          height={28}
                        />
                      ))}
                  </div>
                </div>
              </Checkbox>
            </div>
          ))}
        </div>

        <div className="d-flex flex-col gap-10 transport-mode-container mt-40">
          <Typography.Title level={5} className="fw-600 color-25272A mb-0 lh-normal">
            {intl.formatMessage({ id: 'modeOfTransportationAtDestination' })}
          </Typography.Title>
          {FMLM_FILTER_TRANSPORT_MODES.map((mode) => (
            <div className="checkbox-container d-flex w-100 align-items-center justify-content-between" key={mode.label}>
              <Checkbox
                className="checkbox-21 w-100"
                value={mode.value}
                checked={fmlmFilter.destinationTransportMode.includes(mode.value)}
                onChange={handleChangeTransportationMode("destinationTransportMode")}
                disabled={mode.disabled}
              >
                <div className="d-flex align-items-center justify-content-between w-100">
                  <span className="fs-16 ff-inter fw-500 ml-5 lh-normal">
                    {intl.formatMessage({ id: mode.translateKey, defaultMessage: mode.label })}
                  </span>
                  <div>
                    {
                      mode.modes.map((transitMode) => (
                        <Image
                          key={transitMode}
                          src={getTransitIconPath(transitMode, "red")}
                          alt={transitMode}
                          width={28}
                          height={28}
                        />
                      ))}
                  </div>
                </div>
              </Checkbox>
            </div>
          ))}
        </div>

        <div className="d-flex flex-col gap-10 transport-mode-container mt-40">
          <Typography.Title level={5} className="fw-600 color-25272A mb-0 lh-normal">
            {intl.formatMessage({ id: 'sortBy' })}
          </Typography.Title>
          <Radio.Group value={fmlmFilter.sortBy} className="d-flex flex-col gap-10" onChange={handleSortByChange}>
            {FMLM_FILTER_SORT_OPTIONS.map((option) => (
              <div className="checkbox-container d-flex w-100 align-items-center justify-content-between" key={option.label}>
                <Radio
                  className="checkbox-21 w-100"
                  value={option.value}
                  disabled={option.disabled}
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <span className="fs-16 ff-inter fw-500 ml-5 lh-normal">
                      {intl.formatMessage({ id: option.translationKey, defaultMessage: option.label })}
                    </span>
                  </div>
                </Radio>
              </div>
            ))}
          </Radio.Group>
        </div>

        <div className="mt-40 mb-20 d-flex gap-5">
          {isFmlmFilterActive ?
            <>
              <Button type="text" size="large" className="w-100 fw-600 ff-inter h-50" onClick={handleClearFilter}>
                {intl.formatMessage({ id: "clearFilter" })}
              </Button>
              <Button type="primary" size="large" className="w-100 fw-600 ff-inter h-50" onClick={handleGetOffers}>
                {intl.formatMessage({ id: "showResults" })}
              </Button>
            </> :
            <Button type="primary" size="large" className="w-100 fw-600 ff-inter h-50" onClick={handleGetOffers}>
              {intl.formatMessage({ id: "done" })}
            </Button>
          }
        </div>
      </div>
    </Drawer>
  );
}

export default FmlmFilterDrawer;
